'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { cx } from 'class-variance-authority';
import { graphql } from '@funkey/types/graphql/gql';
import { FormGroup, FormInputErrors, Input, Modal, Textarea, Button } from '~/components/primitives';
import type { I18n } from '@funkey/locales';
import { api } from '~/modules';
import { useI18n } from '~/features/i18n';
import Title from '~/components/utils/Title';
import ReviewStars from '~/components/primitives/ReviewStars';
import useNotifications from '~/features/notifications/useNotifications';
import { useReCAPTCHA } from '~/features/form/recaptcha/client';
import { useFormState } from '~/modules/form';
import { std } from '@funkey/shared';
import z from 'zod';
export default function ActivityReviews(t0) {
  const $ = _c(53);
  const {
    limit,
    activityId
  } = t0;
  const i18n = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const t1 = Number(limit);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      rating: "DESC"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== activityId || $[2] !== t1) {
    t3 = {
      first: t1,
      activityId,
      orderBy: t2
    };
    $[1] = activityId;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    activityReviews,
    isLoading,
    onFetchMore,
    isLoadingMore,
    hasMoreRecords
  } = useActivityPageReviewsQuery(t3);
  const t4 = activityId ?? "0";
  let t5;
  if ($[4] !== t4) {
    t5 = {
      activityId: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data: reviews
  } = api.useGraphqlQuery(QUERIES.averageReviewRating, t5);
  const {
    mutateAsync
  } = api.useGraphqlMutation(MUTATION);
  const {
    dispatchSuccess
  } = useNotifications();
  let t6;
  if ($[6] !== hasMoreRecords || $[7] !== onFetchMore) {
    t6 = async () => {
      if (hasMoreRecords) {
        await onFetchMore();
      }
    };
    $[6] = hasMoreRecords;
    $[7] = onFetchMore;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const loadMoreReviews = t6;
  if (isLoading) {
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div>Loading...</div>;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const t7 = !hasMoreRecords && "pb-12 lg:pb-20";
  let t8;
  if ($[10] !== t7) {
    t8 = cx("relative pt-10 text-blue", t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== i18n) {
    t9 = i18n._("Reviews for this activity");
    $[12] = i18n;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <Title size="5xl" className="mb-0 text-center">{t9}</Title>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== activityReviews?.length || $[17] !== i18n || $[18] !== reviews) {
    t11 = activityReviews?.length ? <div className="mb-6 flex items-center justify-center gap-2"><div className="flex flex-row"><ReviewStars rating={reviews?.averageReviewRating?.average || 0} /></div><div className="mt-1 font-medium">{i18n._("{average}/5 of", {
          average: reviews?.averageReviewRating?.average ? std.Number.toString(reviews.averageReviewRating.average) : "-"
        })}{" "}{i18n._("{total} reviews", {
          total: reviews?.averageReviewRating?.total
        })}</div></div> : null;
    $[16] = activityReviews?.length;
    $[17] = i18n;
    $[18] = reviews;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== activityReviews || $[21] !== i18n) {
    t12 = activityReviews?.length ? <div className="gap-5 sm:columns-2 md:columns-3 lg:columns-4">{activityReviews?.map(_temp)}</div> : <p className="my-6">{i18n._("activity.reviews.empty_state")}</p>;
    $[20] = activityReviews;
    $[21] = i18n;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== i18n || $[24] !== isLoadingMore) {
    t13 = isLoadingMore && <div> {i18n._("Loading reviews")} ...</div>;
    $[23] = i18n;
    $[24] = isLoadingMore;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== hasMoreRecords || $[27] !== i18n || $[28] !== loadMoreReviews) {
    t14 = hasMoreRecords && <Button variant="tertiary" onClick={loadMoreReviews}>{i18n._("Show more reviews")}</Button>;
    $[26] = hasMoreRecords;
    $[27] = i18n;
    $[28] = loadMoreReviews;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsOpen(true);
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== i18n) {
    t16 = i18n._("Write a review");
    $[31] = i18n;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = <Button variant="secondary" onClick={t15}>{t16}</Button>;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t14 || $[36] !== t17) {
    t18 = <div className="absolute bottom-0 left-0 h-[175px] w-full bg-linear-to-b from-transparent to-white"><div className="mt-6 flex h-full items-center justify-center gap-6">{t14}{t17}</div></div>;
    $[35] = t14;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== activityId || $[39] !== dispatchSuccess || $[40] !== i18n || $[41] !== isLoading || $[42] !== isOpen || $[43] !== mutateAsync) {
    t19 = isOpen && <ReviewPopup onClose={() => setIsOpen(false)} onSubmit={async input => {
      await mutateAsync({
        activityId,
        input
      }).then(() => setIsOpen(false));
      dispatchSuccess({
        title: i18n._("Review successfully created")
      });
    }} isLoading={isLoading} />;
    $[38] = activityId;
    $[39] = dispatchSuccess;
    $[40] = i18n;
    $[41] = isLoading;
    $[42] = isOpen;
    $[43] = mutateAsync;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t18 || $[50] !== t19 || $[51] !== t8) {
    t20 = <div className={t8}>{t10}{t11}{t12}{t13}{t18}{t19}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t18;
    $[50] = t19;
    $[51] = t8;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}
function _temp(t0) {
  const {
    id,
    rating,
    review,
    reviewerName,
    createdAt
  } = t0;
  return <div key={id} className="mb-8 break-inside-avoid-column text-left"><div className="mb-5 rounded-2xl bg-blue-50 p-8"><div className="mb-0.5 -ml-1 flex -space-x-1"><ReviewStars rating={rating ?? 0} /></div><p>{review}</p></div><div className="flex items-center"><div className="flex flex-col"><span className="text-primary">{reviewerName}</span><span className="text-xs text-blue-500">{timeAgo(createdAt)}</span></div></div></div>;
}
const ReviewPopup = t0 => {
  const $ = _c(35);
  const {
    onClose,
    onSubmit,
    isLoading
  } = t0;
  const i18n = useI18n();
  const recaptcha = useReCAPTCHA();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      reviewerName: "",
      reviewerEmail: "",
      rating: 0,
      review: "",
      reviewerCompany: "",
      privateFeedback: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== i18n) {
    t2 = Object.entries({
      reviewerName: {
        validator: z.string().min(2),
        message: i18n._("Name is too short")
      },
      reviewerEmail: {
        validator: z.email(),
        message: i18n._("Invalid email address")
      },
      rating: {
        validator: z.number().min(1).max(5),
        message: i18n._("Score must be between 1 and 5")
      },
      review: {
        validator: z.string().min(1),
        message: i18n._("Review text is required")
      }
    }).reduce(_temp2, {});
    $[1] = i18n;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formState = useFormState(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Modal.Header title="Jouw review" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== formState || $[5] !== i18n) {
    let t5;
    if ($[7] !== formState) {
      t5 = t6 => {
        const {
          label,
          field,
          className,
          type
        } = t6;
        return <FormGroup key={field} label={label} htmlFor={field} className={className} variant="no_spacing">{type === "Input" && <Input {...formState.getNativeInputProps(field)} />}{type === "Textarea" && <Textarea {...formState.getNativeInputProps(field)} />}{type === "custom" && <ReviewStars rating={formState.values.rating} onChange={value_0 => formState.handleChange(field, value_0)} />}<FormInputErrors errors={formState.getErrorMessages(field)} /></FormGroup>;
      };
      $[7] = formState;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = MODAL_FIELDS(i18n).map(t5);
    $[4] = formState;
    $[5] = i18n;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[9] !== recaptcha.Component) {
    t5 = <div className="col-span-1 sm:col-span-2"><recaptcha.Component /></div>;
    $[9] = recaptcha.Component;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <Modal.Content className="grid h-full grid-cols-1 gap-5 sm:grid-cols-2">{t4}{t5}</Modal.Content>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== i18n) {
    t7 = i18n._("Cancel");
    $[14] = i18n;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onClose || $[17] !== t7) {
    t8 = <Button size="sm" variant="outline" onClick={onClose}>{t7}</Button>;
    $[16] = onClose;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isLoading || !recaptcha.isValidated;
  let t10;
  if ($[19] !== formState || $[20] !== onSubmit) {
    t10 = () => {
      if (formState.validate()) {
        onSubmit(formState.values);
      }
    };
    $[19] = formState;
    $[20] = onSubmit;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== i18n) {
    t11 = i18n._("Send review");
    $[22] = i18n;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <Button size="sm" disabled={t9} onClick={t10}>{t11}</Button>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t8) {
    t13 = <Modal.Footer>{t8}{t12}</Modal.Footer>;
    $[28] = t12;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== onClose || $[32] !== t13 || $[33] !== t6) {
    t14 = <Modal onClose={onClose} className="h-full max-w-xl">{t3}{t6}{t13}</Modal>;
    $[31] = onClose;
    $[32] = t13;
    $[33] = t6;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};
const MODAL_FIELDS = (i18n: I18n) => [{
  label: i18n._('Name'),
  field: 'reviewerName',
  className: 'col-span-1',
  type: 'Input'
}, {
  label: i18n._('Email address'),
  field: 'reviewerEmail',
  className: 'col-span-1',
  type: 'Input'
}, {
  label: i18n._('Your score'),
  field: 'rating',
  className: 'col-span-1 sm:col-span-2',
  type: 'custom'
}, {
  label: i18n._('Review'),
  field: 'review',
  className: 'col-span-1 sm:col-span-2',
  type: 'Textarea'
}, {
  label: i18n._('Company'),
  field: 'reviewerCompany',
  className: 'col-span-1 sm:col-span-2',
  type: 'Input'
}, {
  label: i18n._('Feedback for Funkey'),
  field: 'privateFeedback',
  className: 'col-span-1 sm:col-span-2',
  type: 'Textarea'
}] as const satisfies {
  label: string;
  field: keyof api.GraphQLDocument.input<typeof MUTATION>['input'];
  className: string;
  type: 'Input' | 'Textarea' | 'custom';
}[];
const timeAgo = (date: string): string => {
  const parsedDate = new Date(date);
  return formatDistanceToNow(parsedDate, {
    addSuffix: true
  });
};
const QUERIES = {
  averageReviewRating: graphql(`
        query AverageReviewRating($activityId: ID!) {
            averageReviewRating(activityId: $activityId) {
                average
                total
            }
        }
    `),
  activityReviews: graphql(`
        query ActivityReviews(
            $activityId: ID
            $after: String
            $first: Int
            $orderBy: ReviewOrderBy
        ) {
            activityReviews(
                activityId: $activityId
                after: $after
                first: $first
                orderBy: $orderBy
            ) {
                edges {
                    cursor
                    node {
                        id
                        activityId
                        isApproved
                        reviewerName
                        rating
                        review
                        createdAt
                    }
                }
                pageInfo {
                    ...PageInfo
                }
            }
        }
    `)
};
const MUTATION = graphql(`
    mutation CreateReview($activityId: ID!, $input: ReviewInput!) {
        createReview(activityId: $activityId, input: $input)
    }
`);
function useActivityPageReviewsQuery(variables: api.GraphQLDocument.input<typeof QUERIES.activityReviews>, options?: api.UseGraphqlInfiniteQueryOptions) {
  const {
    error,
    isLoading,
    refetch,
    isFetchingNextPage,
    hasMoreRecords,
    fetchNextPage,
    activityReviews
  } = api.useGraphqlInfiniteQuery(QUERIES.activityReviews, variables, 'activityReviews', options);
  return {
    error,
    isLoading,
    activityReviews,
    refetch,
    isLoadingMore: isFetchingNextPage,
    hasMoreRecords,
    onFetchMore: () => fetchNextPage()
  };
}
function _temp2(acc, t0) {
  const [key, t1] = t0;
  const {
    validator,
    message
  } = t1;
  return {
    ...acc,
    [key]: {
      validate: value => validator.safeParse(value).success,
      message
    }
  };
}